/**
 * CookieBanner — single-banner consent UI.
 *
 * Displayed exactly once until the user makes a decision. After that the
 * decision can be revisited via `openConsentSettings()` (footer link or
 * privacy page CTA), which re-opens the same component as a centred
 * dialog instead of a corner banner.
 *
 * Mounted once at the App root — uses portal-friendly fixed positioning.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  onConsentOpenRequest,
  useConsent,
} from "@/lib/consent";
import { useI18n } from "@/i18n";

export function CookieBanner() {
  const { t } = useI18n();
  const { status, accept, reject } = useConsent();
  // Re-open requests from elsewhere (footer link, privacy page) force the
  // banner back to visible even after the user already made a decision.
  const [forceOpen, setForceOpen] = useState(false);

  useEffect(() => {
    return onConsentOpenRequest(() => setForceOpen(true));
  }, []);

  const visible = status === "unknown" || forceOpen;
  if (!visible) return null;

  const close = () => setForceOpen(false);
  const handleAccept = () => {
    accept();
    close();
  };
  const handleReject = () => {
    reject();
    close();
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="consent-title"
      className="fixed inset-x-0 bottom-0 z-[300] px-3 pb-3 md:px-6 md:pb-6 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-3xl rounded-2xl border border-[hsl(var(--pelagic)/0.25)] bg-[hsl(var(--abyss))] text-[hsl(var(--salt-white))] shadow-2xl">
        <div className="flex items-start gap-3 p-4 md:p-5">
          <div className="hidden md:flex shrink-0 h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--pelagic)/0.18)] text-[hsl(var(--pelagic))]">
            <Cookie className="h-5 w-5" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <h2
                id="consent-title"
                className="font-heading text-base md:text-lg font-semibold leading-tight"
              >
                {t("consent.title")}
              </h2>
              {forceOpen && status !== "unknown" && (
                <button
                  type="button"
                  onClick={close}
                  className="text-[hsl(var(--sand-grey)/0.7)] hover:text-[hsl(var(--salt-white))] transition-colors"
                  aria-label={t("common.close")}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="mt-2 text-sm leading-relaxed text-[hsl(var(--sand-grey))]">
              {t("consent.body")}{" "}
              <Link
                to="/datenschutz"
                className="underline underline-offset-2 hover:text-[hsl(var(--salt-white))]"
              >
                {t("consent.privacyLink")}
              </Link>
              .
            </p>

            {status !== "unknown" && (
              <p className="mt-2 text-xs text-[hsl(var(--sand-grey)/0.7)]">
                {status === "granted"
                  ? t("consent.currentGranted")
                  : t("consent.currentDenied")}
              </p>
            )}

            <div className="mt-4 flex flex-col-reverse md:flex-row md:items-center gap-2 md:gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReject}
                className="border-[hsl(var(--pelagic)/0.4)] bg-transparent text-[hsl(var(--salt-white))] hover:bg-[hsl(var(--pelagic)/0.15)] hover:text-[hsl(var(--salt-white))]"
              >
                {t("consent.reject")}
              </Button>
              <Button
                size="sm"
                onClick={handleAccept}
                className="bg-[hsl(var(--pelagic))] text-white hover:bg-[hsl(var(--pelagic)/0.9)]"
              >
                {t("consent.accept")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookieBanner;